import type {MithraMaterializedApi} from "../../services/MithraMaterializedApi";
import type {CategorizationReviewStore} from "./CategorizationReviewStore";
import type AuthStore from "../../authentication/AuthStore";
import {makeAutoObservable, reaction} from "mobx";
import {AxoisRequestManager} from "../managers/RequestManager";
import type {Subscription} from "rxjs";
import {catchError, defer, from, of, repeat, retry, Subject, switchMap, tap} from "rxjs";
import type {CategorizationApprovalEstimate, CategorizationStatus} from "../../services/classes/CategorizationClasses";
import {getStatusIsFinal} from "../../services/classes/JobClasses";
import {environment, rollbar} from "../../env";
import type {RunningJobStatusResponse} from "../../services/classes/AiClasses";
import {notificationStore} from "../init";
import {NotificationStore} from "../NotificationStore";
import {AxiosError} from "axios";
import {useMithraAuthContext} from "../../authentication/AuthContext.ts";
import {useNavigate} from "react-router";

export const statusTrigger = new Subject<void>();
let statusSubscription: Subscription | undefined;


/**
 * This class is responsible for the communication with the backend regarding the status of the categorization
 */
export class CategorizationStatusDelegate {
    egoApprovalTimer?: NodeJS.Timeout;

    /**
     * Request for checking of the review screen is available
     * Note: This endpoint should be accessible by non-super users as well
     */
    readonly statusRequestManager = new AxoisRequestManager<{databagId: number}, CategorizationStatus>(
        ({databagId}) => from(this.api.getApprovalStatus(databagId)).pipe(tap(resp => {
            const job = resp.data.categorization_status.latest_categorization_job;
            if (job) {
                if (!getStatusIsFinal(job.current_status.status)) {
                    this.categorizationReviewStore.reviewPageController.reviewPageStateController.setPage('ai-busy')
                }
            }
        }))
    );

    constructor(
        private api: MithraMaterializedApi,
        private categorizationReviewStore: CategorizationReviewStore,
        private auth: AuthStore,
    ) {
        makeAutoObservable(this);

        // Be careful: We stay subscribed to this subject
        // TODO: CAT-1580 loading and initialization is convoluted
        //  this leads to double execution on hot-loading in dev setup
        if (statusSubscription) {
            statusSubscription.unsubscribe();
        }
        statusSubscription = statusTrigger.pipe(
            // When statusTrigger occurs we start polling
            switchMap(() =>
                defer(() => from(this.api.getRunningJobs())).pipe(
                    repeat({
                        count: environment.apiStatusPollingInterval > 0 ? undefined : 0,
                        delay: environment.apiStatusPollingInterval,
                    }),
                    catchError(error => {
                        if (error instanceof AxiosError && error.response && error.response.status === 401) {
                            statusSubscription?.unsubscribe();
                            this.auth.authentication.logout(useMithraAuthContext(), useNavigate());
                            return of(error.response);
                        }
                        console.log('err');
                        rollbar.error(error);
                        throw error;
                    }),
                    retry({
                        delay: environment.apiStatusPollingInterval,
                        count: environment.apiStatusPollingInterval > 0 ? undefined : 0,
                    })
                )),
        ).subscribe(
            resp => this.setRunningJobs(resp.data),
        )

        reaction(() => [this.categorizationReviewStore.bagId], ([_bagId]) => {
            this.stopCheckingStatus()
            this.checkStatus();
            if (this.auth.user)
                statusTrigger.next();
        })
    }

    /**
     * Very crude check if some approval job is running.
     *
     * When there is a job running each component on the dashboard needs to get a disclaimer that the reviews are being applied.
     */
    runningJobs?: RunningJobStatusResponse;

    get isSomeApprovalJobRunning() {
        return Boolean(this.runningJobs?.running_categorization_jobs);
    }

    get isCategorizationBeingUpdated() {
        return Boolean(this.runningJobs?.running_categorization_jobs)
            || Boolean(this.runningJobs?.running_nightly_jobs)
    }

    get status(): CategorizationStatus | undefined {
        return this.statusRequestManager.result;
    }

    get latestCategorizationJob() {
        return this.status?.categorization_status?.latest_categorization_job;
    }

    get taxonomyStatus() {
        return this.status?.taxonomy_status;
    }

    get categorizationEstimate(): CategorizationApprovalEstimate | undefined {
        return this.status?.categorization_status?.estimate;
    }

    get isCategorizationApprovalBusy(): boolean {
        if (!this.categorizationEstimate) return false;
        return this.categorizationEstimate.n_busy_requests > 0;
    }

    get categorizationJobId() {
        return this.latestCategorizationJob?.id;
    }

    get categorizationJobStatus() {
        return this.latestCategorizationJob?.current_status.status
    }

    get latestRevisionId(): number | null {
        return this.latestCategorizationJob?.current_revision_id || null;
    }

    get latestRevisionIsRejected(): boolean {
        return Boolean(this.latestCategorizationJob?.current_revision_is_rejected);
    }

    get latestRevisionIsNotRejected(): boolean {
        return this.latestCategorizationJob?.current_revision_is_rejected === false;
    }

    startCheckingStatus() {
        clearInterval(this.egoApprovalTimer);
        if(environment.apiStatusPollingInterval > 0) {
            this.egoApprovalTimer = setInterval(() => this.checkStatus(), environment.apiStatusPollingInterval);
        }
        this.checkStatus();
    }

    stopCheckingStatus() {
        clearInterval(this.egoApprovalTimer);
    }

    checkStatus() {
        if (this.categorizationReviewStore.bagId > 0) {
            this.statusRequestManager.request({databagId: this.categorizationReviewStore.bagId})
        } else {
            this.statusRequestManager.cleanup()
        }
    }

    checkStatusObs() {
        if (this.categorizationReviewStore.bagId > 0) {
            return this.statusRequestManager.requestObs({databagId: this.categorizationReviewStore.bagId})
        } else {
            this.statusRequestManager.cleanup()
        }
    }

    setRunningJobs(runningJobs: RunningJobStatusResponse) {
        this.runningJobs = runningJobs;

        if(this.isCategorizationBeingUpdated) {
            notificationStore.show(
                'Category changes are being applied. Some information may not show the latest categories yet. Please check back shortly',
                {id: NotificationStore.CATEGORIZATION_MSG_ID}
            )
        } else {
            notificationStore.hide(NotificationStore.CATEGORIZATION_MSG_ID);
        }
    }

    get isFinal(): boolean | null {
        return this.categorizationJobStatus ? getStatusIsFinal(this.categorizationJobStatus) : null;
    }
}